import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import GetRequest from "../../../Api/GetRequest";

export const errorMessage = (response) => {
  if (response?.message) return response.message;
  if (response?.error) return String(response.error);
  if (response?.status) return `The server answered with status ${response.status}.`;
  return "Could not reach the server. Check your connection and try again.";
};

// GET one ops endpoint and keep its envelope `data`. Pass a null url to stay idle.
// A reload of the same url keeps the earlier data on screen; a new url clears it,
// so numbers for old filters (or another order) are never shown as current.
const useD2cGet = (url) => {
  const { accessToken } = useSelector((state) => state.adminReducers);
  const [state, setState] = useState({ loading: false, error: null, data: null, url: null });
  const latest = useRef(0);

  const load = useCallback(async () => {
    const ticket = latest.current + 1;
    latest.current = ticket;
    if (!url) {
      setState({ loading: false, error: null, data: null, url: null });
      return;
    }
    setState((previous) => ({
      loading: true,
      error: null,
      data: previous.url === url ? previous.data : null,
      url,
    }));
    const response = await GetRequest(url, accessToken);
    if (ticket !== latest.current) return; // a newer request or an unmount won
    if (response?.status === 200) {
      setState({ loading: false, error: null, data: response?.data ?? null, url });
    } else {
      setState({
        loading: false,
        data: null,
        url,
        error: { status: response?.status, message: errorMessage(response) },
      });
    }
  }, [url, accessToken]);

  useEffect(() => {
    load();
    return () => {
      latest.current += 1;
    };
  }, [load]);

  // Until the effect for a new url has run, report loading rather than empty.
  const current = Boolean(url) && state.url === url;
  return {
    loading: Boolean(url) && (state.loading || !current),
    error: current ? state.error : null,
    data: current ? state.data : null,
    reload: load,
  };
};

export default useD2cGet;
